import { HDInsightOnDemandLinkedService } from 'app/model/linkedservice/hdinsightondemand.linkedservice';
import { AdapterRegistry } from 'app/model/adapters/adapter-registry';
import { ReferenceAdapter } from 'app/model/adapters/reference.adapter';
import { Model } from 'app/model/backendmodel';
import { IModel } from 'app/model/base/annotation';
import { Metamodel } from 'app/model/base/model';
import { AzureStorageLinkedService } from 'app/model/linkedservice/azure-storage.linkedservice';
import { LinkedServiceAdapter } from 'app/model/adapters/linkedservices/linkedservice.adapter';
import { toParameterClass, toParameterObject } from 'app/model/adapters/linkedservices/parameter-config.util';
import { passwordOrAKVtoSecretBase, secretBaseToPasswordOrAKV } from './azure-key-vault.util';
import { linkedServiceReferenceWithoutParameterizationRemainer } from '../reference-remainer';
import { stringIsNullOrEmpty } from '../../../shared/utils/string.util';
import { HDInsightOndemandClusterResourceGroupAuthTypes, Credential } from 'shell-models-lib';

@AdapterRegistry.adapter({
    model: Metamodel.modelFor(HDInsightOnDemandLinkedService),
    asynchronous: true,
    adapts: {
        name: '*',
        properties: {
            description: '*',
            type: '*',
            connectVia: '*',
            typeProperties: {
                clusterSize: '*',
                timeToLive: '*',
                version: '*',
                linkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
                hostSubscriptionId: '*',
                servicePrincipalId: '*',
                servicePrincipalKey: 'secret',
                tenant: '*',
                clusterResourceGroup: '*',
                clusterNamePrefix: '*',
                clusterUserName: '*',
                clusterPassword: 'secret',
                clusterSshUserName: '*',
                clusterSshPassword: 'secret',
                additionalLinkedServiceNames: linkedServiceReferenceWithoutParameterizationRemainer,
                hcatalogLinkedServiceName: linkedServiceReferenceWithoutParameterizationRemainer,
                clusterType: '*',
                osType: '*',
                scriptActions: '*',
                sparkVersion: '*',
                coreConfiguration: '*',
                hBaseConfiguration: '*',
                hdfsConfiguration: '*',
                hiveConfiguration: '*',
                mapReduceConfiguration: '*',
                oozieConfiguration: '*',
                stormConfiguration: '*',
                yarnConfiguration: '*',
                encryptedCredential: '*',
                headNodeSize: '*',
                dataNodeSize: '*',
                zookeeperNodeSize: '*',
                subnetName: '*',
                virtualNetworkId: '*',
                clusterResourceGroupAuthType: '*',
                credential: '*'
            }
        }
    }
})
export class HDInsightOnDemandLinkedServiceAdapter extends LinkedServiceAdapter<HDInsightOnDemandLinkedService> {
    protected modelConstructor(): new () => HDInsightOnDemandLinkedService {
        return HDInsightOnDemandLinkedService;
    }

    protected model(): IModel {
        return HDInsightOnDemandLinkedService.MODEL;
    }

    protected async propertiesToResourceAsync(ls: HDInsightOnDemandLinkedService, linkedServiceResource: Model.LinkedServiceResource): Promise<Model.LinkedServiceResource> {
        const properties = this.properties(linkedServiceResource) as Model.HDInsightOnDemandLinkedService;

        properties.type = Metamodel.modelFor(HDInsightOnDemandLinkedService).name;
        properties.typeProperties = {
            // Cluster properties
            clusterType: ls.clusterType,
            sparkVersion: ls.sparkVersion,
            clusterSize: ls.clusterSize != null ? parseInt(ls.clusterSize, 10) : undefined,
            clusterNamePrefix: ls.clusterNamePrefix,
            timeToLive: ls.timeToLive,
            version: ls.version,
            osType: ls.clusterSshUserName ? ls.osType : undefined,

            // Resource Group properties
            hostSubscriptionId: ls.hostSubscriptionId,
            clusterResourceGroup: ls.clusterResourceGroup,

            // Storage properties
            linkedServiceName: ReferenceAdapter.toResource(ls.storageLinkedService || new AzureStorageLinkedService(), this.context) as Model.LinkedServiceReference,
            additionalLinkedServiceNames: ls.additionalLinkedServices != null ? ls.additionalLinkedServices.map((v) =>
                ReferenceAdapter.toResource(v || new AzureStorageLinkedService(), this.context) as Model.LinkedServiceReference
            ) : undefined,
            hcatalogLinkedServiceName: ls.hcatalogLinkedService != null ?
                ReferenceAdapter.toResource(ls.hcatalogLinkedService, this.context) as Model.LinkedServiceReference : undefined,
                
            // Additional properties
            clusterUserName: ls.clusterUserName || undefined,
            clusterPassword: passwordOrAKVtoSecretBase(ls.clusterPassword, this.context),
            clusterSshUserName: ls.clusterSshUserName || undefined,
            clusterSshPassword: passwordOrAKVtoSecretBase(ls.clusterSshPassword, this.context),
            scriptActions: (!stringIsNullOrEmpty(ls.scriptName) && !stringIsNullOrEmpty(ls.scriptUri) && ls.scriptNodeTypes.length > 0) ? [{
                name: ls.scriptName,
                uri: ls.scriptUri,
                parameters: ls.scriptParameters || undefined,
                roles: ls.scriptNodeTypes
            }] : undefined,
            
            headNodeSize: ls.headNodeSize,
            dataNodeSize: ls.dataNodeSize,
            zookeeperNodeSize: ls.zookeeperNodeSize,
            subnetName: ls.subnetName,
            virtualNetworkId: ls.virtualNetworkId
        };

        properties.typeProperties.clusterResourceGroupAuthType = ls.clusterResourceGroupAuthType;

        // SPN properties
        properties.typeProperties.tenant =
            ls.clusterResourceGroupAuthType === HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey
                ? ls.tenant
                : undefined;
        properties.typeProperties.servicePrincipalId =
            ls.clusterResourceGroupAuthType === HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey
                ? ls.servicePrincipalId
                : undefined;
        properties.typeProperties.servicePrincipalKey =
            ls.clusterResourceGroupAuthType === HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey
                ? passwordOrAKVtoSecretBase(ls.servicePrincipalKey, this.context)
                : undefined;

        // UAMI Credential
        properties.typeProperties.credential =
            ls.clusterResourceGroupAuthType ===
                HDInsightOndemandClusterResourceGroupAuthTypes.UserAssignedManagedIdentity && ls.credential
                ? (ReferenceAdapter.toResource(ls.credential, this.context) as Model.CredentialReference)
                : undefined;

        properties.typeProperties.coreConfiguration = toParameterObject(ls.coreConfiguration);
        properties.typeProperties.hBaseConfiguration = toParameterObject(ls.hbaseConfiguration);
        properties.typeProperties.hdfsConfiguration = toParameterObject(ls.hdfsConfiguration);
        properties.typeProperties.hiveConfiguration = toParameterObject(ls.hiveConfiguration);
        properties.typeProperties.mapReduceConfiguration = toParameterObject(ls.mapReduceConfiguration);
        properties.typeProperties.oozieConfiguration = toParameterObject(ls.oozieConfiguration);
        properties.typeProperties.stormConfiguration = toParameterObject(ls.stormConfiguration);
        properties.typeProperties.yarnConfiguration = toParameterObject(ls.yarnConfiguration);

        return linkedServiceResource;
    }

    protected async propertiesToModelAsync(linkedServiceResource: Model.LinkedServiceResource, ls: HDInsightOnDemandLinkedService): Promise<HDInsightOnDemandLinkedService> {
        const properties = linkedServiceResource.properties as Model.HDInsightOnDemandLinkedService;
        ls.clusterType = properties.typeProperties.clusterType;
        ls.version = properties.typeProperties.version;
        ls.clusterSize = properties.typeProperties.clusterSize != null ? JSON.stringify(properties.typeProperties.clusterSize) : undefined;
        ls.timeToLive = properties.typeProperties.timeToLive;
        ls.hostSubscriptionId = properties.typeProperties.hostSubscriptionId;
        ls.clusterResourceGroup = properties.typeProperties.clusterResourceGroup;
        ls.servicePrincipalId = properties.typeProperties.servicePrincipalId;
        ls.tenant = properties.typeProperties.tenant;
        ls.osType = properties.typeProperties.osType;
        ls.clusterNamePrefix = properties.typeProperties.clusterNamePrefix;
        ls.clusterUserName = properties.typeProperties.clusterUserName;
        ls.clusterPassword = secretBaseToPasswordOrAKV(properties.typeProperties.clusterPassword, properties.typeProperties.clusterUserName, this.context);
        ls.clusterSshUserName = properties.typeProperties.clusterSshUserName;
        ls.clusterSshPassword = secretBaseToPasswordOrAKV(properties.typeProperties.clusterSshPassword, properties.typeProperties.clusterSshUserName, this.context);
        if (properties.typeProperties.scriptActions && properties.typeProperties.scriptActions.length > 0) {
            const scriptAction = properties.typeProperties.scriptActions[0];
            if (scriptAction != null && !stringIsNullOrEmpty(scriptAction.name)) {
                ls.scriptName = scriptAction.name;
                ls.scriptUri = scriptAction.uri;
                ls.scriptNodeTypes = scriptAction.roles;
                ls.scriptParameters = scriptAction.parameters;
            }
        }
        ls.sparkVersion = properties.typeProperties.sparkVersion;
        ls.servicePrincipalKey = secretBaseToPasswordOrAKV(properties.typeProperties.servicePrincipalKey, true, this.context);
        ls.additionalLinkedServices = properties.typeProperties.additionalLinkedServiceNames != null &&
            properties.typeProperties.additionalLinkedServiceNames.length > 0 ?
            properties.typeProperties.additionalLinkedServiceNames.map((v) =>
                ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(
                    Metamodel.modelFor(AzureStorageLinkedService),
                    v, this.context)) :
            undefined;
        ls.headNodeSize = properties.typeProperties.headNodeSize;
        ls.dataNodeSize = properties.typeProperties.dataNodeSize;
        ls.zookeeperNodeSize = properties.typeProperties.zookeeperNodeSize;
        ls.subnetName = properties.typeProperties.subnetName;
        ls.virtualNetworkId = properties.typeProperties.virtualNetworkId;
        if (properties.typeProperties.hcatalogLinkedServiceName != null) {
            ls.hcatalogLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                properties.typeProperties.hcatalogLinkedServiceName, this.context);
        }
        if (properties.typeProperties.linkedServiceName != null) {
            ls.storageLinkedService = ReferenceAdapter.toProxyModel<AzureStorageLinkedService>(Metamodel.modelFor(AzureStorageLinkedService),
                properties.typeProperties.linkedServiceName, this.context);
        }

        if (properties.typeProperties) {
            ls.coreConfiguration = toParameterClass(properties.typeProperties.coreConfiguration, ls);
            ls.hbaseConfiguration = toParameterClass(properties.typeProperties.hBaseConfiguration, ls);
            ls.hdfsConfiguration = toParameterClass(properties.typeProperties.hdfsConfiguration, ls);
            ls.hiveConfiguration = toParameterClass(properties.typeProperties.hiveConfiguration, ls);
            ls.mapReduceConfiguration = toParameterClass(properties.typeProperties.mapReduceConfiguration, ls);
            ls.oozieConfiguration = toParameterClass(properties.typeProperties.oozieConfiguration, ls);
            ls.stormConfiguration = toParameterClass(properties.typeProperties.stormConfiguration, ls);
            ls.yarnConfiguration = toParameterClass(properties.typeProperties.yarnConfiguration, ls);
        }

        if (ls.version && ls.version === '3.6') {
            ls.isNotExistingversion = false ;
        }

        ls.clusterResourceGroupAuthType =
            properties.typeProperties.clusterResourceGroupAuthType ??
            HDInsightOndemandClusterResourceGroupAuthTypes.ServicePrincipalKey;
        ls.credential =
            ls.clusterResourceGroupAuthType ===
                HDInsightOndemandClusterResourceGroupAuthTypes.UserAssignedManagedIdentity &&
                properties.typeProperties.credential != null
                    ? ReferenceAdapter.toProxyModel<Credential>(
                        Metamodel.modelFor(Credential),
                        properties.typeProperties.credential,
                        this.context)
                    : undefined;

        return ls;
    }
}
